import React, { useEffect, useState } from 'react'
import { superAxiosRequest } from 'axiosApi'
import { splitNumberDigits } from 'helpers/math'
import useTableFixedHeader from 'hooks/useTableFixedHeader'
import ContentHeader from 'components/UI/ContentHeader/ContentHeader'
import Loader from 'components/ContentBox/Global/Loader/Loader'
import st from 'components/ContentBox/Global/ReportTable/Table.module.scss'
import ExportExcelButton from '../ExportExcelButton/ExportExcelButton'
import { observer } from 'mobx-react'
import useLocalStorageFilter from 'helpers/useLocalStorageFilter'
import AsideFilterStore from 'components/UI/AsideFilter/asideFilterStore'
import SearchBooks from 'components/Modules/Ebook/Books/components/SearchBooks/SearchBooks'
import FilterTextValues from 'components/UI/AsideFilter/FilterTextValues'
import AsideFilter from 'components/UI/AsideFilter/AsideFilter'
import useLocalization from 'hooks/useLocalization'
import { useHistory } from 'react-router-dom'
import style from './KpiBetweenSchools.module.scss'
import { ExcelRowsParams, setCellParams } from '../../../utils/ExcelExport'

const KpiBetweenSchools = () => {
  const [catalog, setCatalog] = useState({})
  const [filter, updateFilter] = useLocalStorageFilter()
  const {
    isOpenFilter,
    closeFilter,
    setTextValues,
    changeTextValue,
    filterTextValues,
  } = AsideFilterStore
  const history = useHistory()

  const {
    refWrapper,
    data,
    setData,
    isLoading,
    setIsLoading,
    getTdClassNames,
  } = useTableFixedHeader()

  const { translate } = useLocalization({
    section: 'groups',
    subsection: 'all_groups',
    dict: {
      filter: 'Filter',
      reset_all: 'Reset all',
      apply: 'Apply',
      search: 'Search',
      start_date: 'Start date',
      end_date: 'End date',
    },
  })

  const filterCnf = [
    {
      type: 'select-multi',
      label: 'Локация',
      nameState: 'location',
      placeholder: 'Локация',
      values:
        catalog.locations?.sort((a, b) => (a.name > b.name ? 1 : -1)) || [],
      option_value_key: 'id',
      option_label_key: 'name',
    },
    {
      type: 'date_day_picker',
      nameState: ['date_from', 'date_until'],
      label: 'Период',
      isRequired: true,
    },
    {
      type: 'type_selector',
      label: 'Формат',
      values: [
        { id: 1, name: 'Онлайн' },
        { id: 0, name: 'Офлайн' },
      ],
      nameState: 'online',
    },
  ]

  // Загрузка каталога локаций (без данных отчета)
  useEffect(() => {
    superAxiosRequest({
      method: 'get',
      link: 'progress_report/kpi_for_school',
    })
      .then(({ data }) => {
        setCatalog({ locations: data.locations })
        setData({ locations: [] })
        setIsLoading(false)
      })
      .catch(() => setIsLoading(false))
  }, [])

  // Загрузка данных при изменении фильтра
  useEffect(() => {
    // Проверяем, что filter существует и выбран обязательный фильтр (период)
    if (!filter || !filter.date_from || !filter.date_until) {
      setData({ locations: [] })
      setIsLoading(false)
      return
    }

    setIsLoading(true)

    const params = {}
    if (filter.date_from) params.date_from = filter.date_from
    if (filter.date_until) params.date_until = filter.date_until
    if (filter.online || filter.online === 0) params.online = filter.online

    // Если выбраны конкретные локации, передаем их в запрос
    if (filter.location && filter.location.length > 0) {
      params.location = Array.isArray(filter.location)
        ? filter.location.join(',')
        : filter.location
    }

    superAxiosRequest({
      method: 'get',
      link: 'progress_report/kpi_for_school',
      params,
    })
      .then(({ data }) => {
        // Фильтруем локации - оставляем только те, для которых есть данные в report
        const locationsWithData = data.locations.filter(
          (location) =>
            data.report[location.id] !== undefined &&
            data.report[location.id] !== null,
        )

        const transformedData = locationsWithData.map((location) => {
          const studentsStartPeriod =
            (data.report[location.id]?.active_student || 0) +
            (data.report[location.id]?.left_student || 0) -
            (data.report[location.id]?.new_student || 0)

          const activeStudent = data.report[location.id]?.active_student || 0
          const countGroup = data.report[location.id]?.count_group || 0

          // ВЫЧИСЛЯЕМ НАПОЛНЯЕМОСТЬ: Студенты на конец периода / Количество групп
          const calculatedFulfill =
            countGroup > 0 ? activeStudent / countGroup : 0

          return {
            id: location.id,
            name: location.name,
            crm_leads_count: data.report[location.id]?.crm_leads_count || 0,
            crm_deals_count: data.report[location.id]?.crm_deals_count || 0,
            conversion: data.report[location.id]?.conversion || 0,
            start_speed: data.report[location.id]?.start_speed || 0,
            study_length: data.report[location.id]?.study_length || 0,
            study_length_hours:
              data.report[location.id]?.study_length_hours || 0,
            active_student: activeStudent,
            new_student: data.report[location.id]?.new_student || 0,
            left_student: data.report[location.id]?.left_student || 0,
            count_group: countGroup,
            fulfill: calculatedFulfill, // Используем вычисленное значение
            sales_hours: data.report[location.id]?.sales_hours || 0,
            consumed: data.report[location.id]?.consumed || 0,
            students_start_period: studentsStartPeriod,
          }
        })

        setData({ locations: transformedData })
        setIsLoading(false)
      })
      .catch(() => {
        setData({ locations: [] })
        setIsLoading(false)
      })
  }, [filter?.date_from, filter?.date_until, filter?.location, filter?.online])

  useEffect(() => {
    return AsideFilterStore.unmountFilter
  }, [])

  useEffect(() => {
    const unlisten = history.listen((location) => {
      if (location.pathname !== '/reports/kpi-between-schools') {
        updateFilter({})
      }
    })
    return () => {
      unlisten()
    }
  }, [history, updateFilter])

  const calculatePercentage = (value, total) => {
    if (!total || total === 0) return '0%'
    const percentage = (value / total) * 100
    return `${percentage.toFixed(1)}%`
  }

  // Инвертированный процент: 100 - (value / total * 100)
  // Используется для колонки "Студенты на конец периода"
  const calculateInvertedPercentage = (value, total) => {
    if (!total || total === 0) return '0%'
    const percentage = (value / total) * 100
    const inverted = 100 - percentage

    return `${Math.abs(inverted).toFixed(1)}%`
  }

  // Функция для расчета итогов с учетом суммы и среднего
  const calculateTotals = (locations) => {
    if (!locations || locations.length === 0) return null

    const count = locations.length

    // Суммируемые поля
    const sumFields = {
      crm_leads_count: 0,
      crm_deals_count: 0,
      students_start_period: 0,
      new_student: 0,
      left_student: 0,
      active_student: 0,
      count_group: 0,
      sales_hours: 0,
      consumed: 0,
    }

    // Для средних значений
    let conversionSum = 0
    let startSpeedSum = 0
    let studyLengthSum = 0
    let studyLengthHourSum = 0

    locations.forEach((loc) => {
      // Суммируемые поля
      sumFields.crm_leads_count += loc.crm_leads_count || 0
      sumFields.crm_deals_count += loc.crm_deals_count || 0
      sumFields.students_start_period += loc.students_start_period || 0
      sumFields.new_student += loc.new_student || 0
      sumFields.left_student += loc.left_student || 0
      sumFields.active_student += loc.active_student || 0
      sumFields.count_group += loc.count_group || 0
      sumFields.sales_hours += loc.sales_hours || 0
      sumFields.consumed += loc.consumed || 0

      // Для средних значений
      conversionSum += loc.conversion || 0
      startSpeedSum += loc.start_speed || 0
      studyLengthSum += loc.study_length || 0
      studyLengthHourSum += loc.study_length_hours || 0
    })

    // ВЫЧИСЛЯЕМ НАПОЛНЯЕМОСТЬ для итога: Сумма студентов на конец периода / Сумма групп
    const totalFulfill =
      sumFields.count_group > 0
        ? sumFields.active_student / sumFields.count_group
        : 0

    return {
      ...sumFields,
      conversion: conversionSum / count,
      start_speed: startSpeedSum / count,
      study_length: studyLengthSum / count,
      study_length_hours: studyLengthHourSum / count,
      fulfill: totalFulfill, // Вычисленное значение
      count: count,
    }
  }

  const downloadExcel = (data) => {
    import('xlsx-js-style').then((XLSX) => {
      // Формируем заголовки с группировкой
      const headers1 = [
        'Школа',
        'Количество лидов',
        'Количество заключенных сделок',
        'Конверсия продаж, %',
        'Скорость запуска (от первого лида до первого занятия) в днях',
        'Продолжительность жизни студента, месяцев',
        'Продолжительность жизни студента в ак. ч.',
        'Студенты на начало периода',
        'Новые студенты',
        'Выбывшие студенты',
        'Студенты на конец периода',
        'Количество групп',
        'Наполняемость групп на конец периода',
        'Проданные ак. часы по новым студентам',
        'Отработанные ак. часы',
      ]

      let tableData = []

      // Данные по школам
      data?.locations?.forEach((location) => {
        let rowData = [
          { v: location.name },
          { v: splitNumberDigits(location.crm_leads_count || '0') },
          { v: splitNumberDigits(location.crm_deals_count || '0') },
          { v: splitNumberDigits(location.conversion?.toFixed(2) || '0.00') },
          { v: splitNumberDigits(location.start_speed?.toFixed(2) || '0.00') },
          { v: splitNumberDigits(location.study_length?.toFixed(2) || '0.00') },
          {
            v: splitNumberDigits(
              location.study_length_hours?.toFixed(2) || '0.00',
            ),
          },
          { v: splitNumberDigits(location.students_start_period || '0') },
          {
            v:
              location.students_start_period > 0
                ? `${splitNumberDigits(location.new_student || '0')} (${((location.new_student / location.students_start_period) * 100).toFixed(1)}%)`
                : splitNumberDigits(location.new_student || '0'),
          },
          {
            v:
              location.students_start_period > 0
                ? `${splitNumberDigits(location.left_student || '0')} (${((location.left_student / location.students_start_period) * 100).toFixed(1)}%)`
                : splitNumberDigits(location.left_student || '0'),
          },
          {
            // ИНВЕРТИРОВАННЫЙ процент
            v:
              location.students_start_period > 0
                ? `${splitNumberDigits(location.active_student || '0')} (${Math.abs(100 - (location.active_student / location.students_start_period) * 100).toFixed(1)}%)`
                : splitNumberDigits(location.active_student || '0'),
          },
          { v: splitNumberDigits(location.count_group || '0') },
          { v: splitNumberDigits(location.fulfill?.toFixed(2) || '0.00') },
          { v: splitNumberDigits(location.sales_hours?.toFixed(2) || '0.00') },
          { v: splitNumberDigits(location.consumed?.toFixed(2) || '0.00') },
        ]
        rowData = setCellParams(rowData, ExcelRowsParams.tableRowParam.cell)
        tableData.push(rowData)
      })

      // Итоговая строка
      if (data?.locations?.length > 0) {
        const totals = calculateTotals(data.locations)

        let totalRow = [
          { v: 'Итого', ...ExcelRowsParams.footerRowParams.firstCell },
          { v: splitNumberDigits(totals.crm_leads_count) },
          { v: splitNumberDigits(totals.crm_deals_count) },
          { v: splitNumberDigits(totals.conversion.toFixed(2)) },
          { v: splitNumberDigits(totals.start_speed.toFixed(2)) },
          { v: splitNumberDigits(totals.study_length.toFixed(2)) },
          { v: splitNumberDigits(totals.study_length_hours.toFixed(2)) },
          { v: splitNumberDigits(totals.students_start_period) },
          {
            v:
              totals.students_start_period > 0
                ? `${splitNumberDigits(totals.new_student)} (${((totals.new_student / totals.students_start_period) * 100).toFixed(1)}%)`
                : splitNumberDigits(totals.new_student),
          },
          {
            v:
              totals.students_start_period > 0
                ? `${splitNumberDigits(totals.left_student)} (${((totals.left_student / totals.students_start_period) * 100).toFixed(1)}%)`
                : splitNumberDigits(totals.left_student),
          },
          {
            // ИНВЕРТИРОВАННЫЙ процент для итога
            v:
              totals.students_start_period > 0
                ? `${splitNumberDigits(totals.active_student)} (${Math.abs(100 - (totals.active_student / totals.students_start_period) * 100).toFixed(1)}%)`
                : splitNumberDigits(totals.active_student),
          },
          { v: splitNumberDigits(totals.count_group) },
          { v: splitNumberDigits(totals.fulfill.toFixed(2)) },
          { v: splitNumberDigits(totals.sales_hours.toFixed(2)) },
          { v: splitNumberDigits(totals.consumed.toFixed(2)) },
        ]

        totalRow = totalRow.map((item, id) =>
          id === 0
            ? item
            : { ...item, ...ExcelRowsParams.footerRowParams.cell },
        )
        tableData.push(totalRow)
      }

      const wb = XLSX.utils.book_new()

      // Создаем данные для листа
      const sheetData = [
        headers1,
        ...tableData.map((row) => row.map((cell) => cell.v)),
      ]

      const ws = XLSX.utils.aoa_to_sheet(sheetData)

      // Настройка ширины колонок
      ws['!cols'] = [
        { wch: 25 }, // Школа
        { wch: 18 }, // Количество лидов
        { wch: 22 }, // Количество заключенных сделок
        { wch: 18 }, // Конверсия продаж, %
        { wch: 45 }, // Скорость запуска
        { wch: 35 }, // Продолжительность жизни студента, месяцев
        { wch: 35 }, // Продолжительность жизни студента в ак. ч.
        { wch: 22 }, // Студенты на начало периода
        { wch: 22 }, // Новые студенты (с процентом)
        { wch: 22 }, // Выбывшие студенты (с процентом)
        { wch: 22 }, // Студенты на конец периода (с процентом)
        { wch: 18 }, // Количество групп
        { wch: 25 }, // Наполняемость групп на конец периода
        { wch: 30 }, // Проданные ак. часы по новым студентам
        { wch: 22 }, // Отработанные ак. часы
      ]

      // Устанавливаем высоту строк
      ws['!rows'] = [
        { hpx: 40 }, // первая строка заголовков
      ]

      // Для остальных строк устанавливаем высоту
      for (let i = 1; i <= sheetData.length - 1; i++) {
        ws['!rows'].push({ hpx: 35 })
      }

      const borderStyle = {
        top: { style: 'thin', color: { rgb: 'B4C6E7' } },
        bottom: { style: 'thin', color: { rgb: 'B4C6E7' } },
        left: { style: 'thin', color: { rgb: 'B4C6E7' } },
        right: { style: 'thin', color: { rgb: 'B4C6E7' } },
      }

      const range = XLSX.utils.decode_range(ws['!ref'])
      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C })
          if (!ws[cellAddress]) continue

          if (R === 0) {
            // Строка заголовков
            ws[cellAddress].s = {
              font: { bold: true, sz: 12, color: { rgb: '1a2c3e' } },
              alignment: {
                horizontal: 'center',
                vertical: 'center',
                wrapText: true,
              },
              fill: { fgColor: { rgb: 'E8E8E8' } },
              border: borderStyle,
            }
          } else if (R === range.e.r) {
            // Итоговая строка
            ws[cellAddress].s = {
              font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } },
              fill: { fgColor: { rgb: '5a8dee' } },
              alignment: { horizontal: 'center', vertical: 'center' },
              border: borderStyle,
            }
          } else {
            // Обычные строки
            ws[cellAddress].s = {
              font: { sz: 10, color: { rgb: '333333' } },
              alignment: { horizontal: 'center', vertical: 'center' },
              border: borderStyle,
            }
          }
        }
      }

      XLSX.utils.book_append_sheet(wb, ws, 'KPI между школами')
      XLSX.writeFile(wb, 'KPI_между_школами.xlsx')
    })
  }

  const locations = data?.locations || []
  const isFilterEmpty = !filterTextValues.length
  const hasRequiredFilters = filter && filter.date_from && filter.date_until

  const totals = locations.length > 0 ? calculateTotals(locations) : null

  return (
    <>
      <ContentHeader title='KPI между школами' />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          margin: '10px',
        }}
      >
        <SearchBooks
          onSearch={(text) => {
            updateFilter({ ...filter, search: text })
            changeTextValue('search', {
              key: 'search',
              label: 'Search',
              value: text,
            })
          }}
          onClear={() => {
            const { search, ...obj } = filter
            updateFilter(obj)
            setTextValues(
              filterTextValues.filter((field) => field.key !== 'search'),
            )
          }}
          translate={translate}
          placeholder='Search...'
          value={filter?.search || ''}
          isFilterEmpty={isFilterEmpty}
        />
        {!!filterTextValues.length && (
          <FilterTextValues
            onDelete={(key) => {
              const { [key]: _, ...obj } = filter
              updateFilter(obj)
            }}
          />
        )}
        {isOpenFilter && (
          <AsideFilter
            translate={translate}
            getFilterParams={updateFilter}
            config={filterCnf}
            filterValue={filter || {}}
            onDropFilter={() => {
              updateFilter({})
              closeFilter()
            }}
          />
        )}
      </div>

      {isLoading ? (
        <Loader />
      ) : !hasRequiredFilters ? (
        <h1 className={st.noData}>Выберите период для отображения данных</h1>
      ) : data?.locations && data.locations.length > 0 ? (
        <>
          <div style={{ padding: '10px', marginBottom: '10px' }}>
            <ExportExcelButton data={data} downloadExcel={downloadExcel} />
          </div>
          <div
            style={{ padding: '10px' }}
            className={st.tableWrapper}
            ref={refWrapper}
          >
            <table className={st.table}>
              <thead className={style.tableHeader}>
                <tr>
                  <th className={style.stickyColumn}>Школа</th>
                  <th>Количество лидов</th>
                  <th>Количество заключенных сделок</th>
                  <th>Конверсия продаж, %</th>
                  <th>
                    Скорость запуска (от первого лида до первого занятия) в днях
                  </th>
                  <th>Продолжительность жизни студента, месяцев</th>
                  <th>Продолжительность жизни студента в ак. ч.</th>
                  <th>Студенты на начало периода</th>
                  <th>Новые студенты</th>
                  <th>Выбывшие студенты</th>
                  <th>Студенты на конец периода</th>
                  <th>Количество групп</th>
                  <th>Наполняемость групп на конец периода</th>
                  <th>Проданные ак. часы по новым студентам</th>
                  <th>Отработанные ак. часы</th>
                </tr>
              </thead>
              <tbody className={`${st.tbody} ${st.tbodyStriped}`}>
                {locations.map((location) => (
                  <tr key={location.id}>
                    <td
                      className={getTdClassNames({
                        classes: [st.color, st.fw500],
                      })}
                    >
                      {location.name}
                    </td>
                    <td className={st.textAlignCenter}>
                      {splitNumberDigits(location.crm_leads_count || '0')}
                    </td>
                    <td className={st.textAlignCenter}>
                      {splitNumberDigits(location.crm_deals_count || '0')}
                    </td>
                    <td className={st.textAlignCenter}>
                      {splitNumberDigits(
                        location.conversion?.toFixed(2) || '0.00',
                      )}
                    </td>
                    <td className={st.textAlignCenter}>
                      {splitNumberDigits(
                        location.start_speed?.toFixed(2) || '0.00',
                      )}
                    </td>
                    <td className={st.textAlignCenter}>
                      {splitNumberDigits(
                        location.study_length?.toFixed(2) || '0.00',
                      )}
                    </td>
                    <td className={st.textAlignCenter}>
                      {splitNumberDigits(
                        location.study_length_hours?.toFixed(2) || '0.00',
                      )}
                    </td>
                    <td className={st.textAlignCenter}>
                      {splitNumberDigits(location.students_start_period || '0')}
                    </td>
                    <td className={st.textAlignCenter}>
                      {splitNumberDigits(location.new_student || '0')}
                      {location.students_start_period > 0 && (
                        <span
                          style={{
                            color: '#666',
                            fontSize: '13px',
                            marginLeft: '4px',
                          }}
                        >
                          (
                          {calculatePercentage(
                            location.new_student,
                            location.students_start_period,
                          )}
                          )
                        </span>
                      )}
                    </td>
                    <td className={st.textAlignCenter}>
                      {splitNumberDigits(location.left_student || '0')}
                      {location.students_start_period > 0 && (
                        <span
                          style={{
                            color: '#666',
                            fontSize: '13px',
                            marginLeft: '4px',
                          }}
                        >
                          (
                          {calculatePercentage(
                            location.left_student,
                            location.students_start_period,
                          )}
                          )
                        </span>
                      )}
                    </td>
                    <td className={st.textAlignCenter}>
                      {splitNumberDigits(location.active_student || '0')}
                      {location.students_start_period > 0 && (
                        <span
                          style={{
                            color: '#666',
                            fontSize: '13px',
                            marginLeft: '4px',
                          }}
                        >
                          (
                          {calculateInvertedPercentage(
                            location.active_student,
                            location.students_start_period,
                          )}
                          )
                        </span>
                      )}
                    </td>
                    <td className={st.textAlignCenter}>
                      {splitNumberDigits(location.count_group || '0')}
                    </td>
                    <td className={st.textAlignCenter}>
                      {splitNumberDigits(
                        location.fulfill?.toFixed(2) || '0.00',
                      )}
                    </td>
                    <td className={st.textAlignCenter}>
                      {splitNumberDigits(
                        location.sales_hours?.toFixed(2) || '0.00',
                      )}
                    </td>
                    <td className={st.textAlignCenter}>
                      {splitNumberDigits(
                        location.consumed?.toFixed(2) || '0.00',
                      )}
                    </td>
                  </tr>
                ))}

                {locations.length > 0 && totals && (
                  <tr className={st.totalRow}>
                    <td className={st.tdTotal}>Итого</td>
                    <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                      {splitNumberDigits(totals.crm_leads_count)}
                    </td>
                    <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                      {splitNumberDigits(totals.crm_deals_count)}
                    </td>
                    <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                      {splitNumberDigits(totals.conversion.toFixed(2))}
                    </td>
                    <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                      {splitNumberDigits(totals.start_speed.toFixed(2))}
                    </td>
                    <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                      {splitNumberDigits(totals.study_length.toFixed(2))}
                    </td>
                    <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                      {splitNumberDigits(totals.study_length_hours.toFixed(2))}
                    </td>
                    <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                      {splitNumberDigits(totals.students_start_period)}
                    </td>
                    <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                      {splitNumberDigits(totals.new_student)}
                      {totals.students_start_period > 0 && (
                        <span
                          style={{
                            color: 'rgba(255,255,255,0.8)',
                            fontSize: '12px',
                            marginLeft: '4px',
                          }}
                        >
                          (
                          {calculatePercentage(
                            totals.new_student,
                            totals.students_start_period,
                          )}
                          )
                        </span>
                      )}
                    </td>
                    <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                      {splitNumberDigits(totals.left_student)}
                      {totals.students_start_period > 0 && (
                        <span
                          style={{
                            color: 'rgba(255,255,255,0.8)',
                            fontSize: '12px',
                            marginLeft: '4px',
                          }}
                        >
                          (
                          {calculatePercentage(
                            totals.left_student,
                            totals.students_start_period,
                          )}
                          )
                        </span>
                      )}
                    </td>
                    <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                      {splitNumberDigits(totals.active_student)}
                      {totals.students_start_period > 0 && (
                        <span
                          style={{
                            color: 'rgba(255,255,255,0.8)',
                            fontSize: '12px',
                            marginLeft: '4px',
                          }}
                        >
                          (
                          {calculateInvertedPercentage(
                            totals.active_student,
                            totals.students_start_period,
                          )}
                          )
                        </span>
                      )}
                    </td>
                    <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                      {splitNumberDigits(totals.count_group)}
                    </td>
                    <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                      {splitNumberDigits(totals.fulfill.toFixed(2))}
                    </td>
                    <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                      {splitNumberDigits(totals.sales_hours.toFixed(2))}
                    </td>
                    <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                      {splitNumberDigits(totals.consumed.toFixed(2))}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <h1 className={st.noData}>Нет данных для выбранных фильтров</h1>
      )}
    </>
  )
}

export default observer(KpiBetweenSchools)
